import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiGet } from './api';
import { STORES_API_BASE } from './useStores';

/**
 * Store OMNI performance (GET /v1/stores/omni). One store's slice of the OMNI
 * Performance report, so the Performance page shows the same numbers as the HQ
 * OMNI report and its XLSX export. A store user resolves its own store; an HQ /
 * dual user passes the header-selected store as `store_no`.
 */

// A metric value this year and the same weekday last year, plus the comp between
// them. Mirrors the retail OMNI Cell: ty (this year), ly_week (the same weekday
// last year, -364 days), comp_week (ty/ly - 1).
//
// The same-calendar-date basis (ly_cal / comp_cal) and the sales plan / bgt_pct
// figures were removed on 2026-08-12 at William's and Franco's direction, which
// is why this page no longer shows a goal bar or a same-date badge.
export type Cell = {
  ty: number | null;
  ly_week: number | null;
  comp_week: number | null;
};

export type TrafficBlock = Cell & { conversion: Cell; days_with_traffic: number | null };
export type DptUptBlock = { dpt: Cell; upt: Cell };

type Windowed<T> = { yesterday: T; wtd: T; mtd: T; ytd: T };

export type Sections = {
  sales: Windowed<Cell>;
  transactions: Windowed<Cell>;
  traffic: Windowed<TrafficBlock>;
  units: Windowed<Cell>;
  dpt_upt: Windowed<DptUptBlock>;
};

export type TrendPoint = { date: string; ty: number; ly: number | null };

/**
 * One row of category momentum. `category` is leadership's product category name
 * (Beverage, Bulk, Chocohol, Dessert Case, Freezer, Gourmet, International,
 * L&P Collection, Non-Edible, Novelty, Retro), mirrored from CounterPoint. It
 * replaced the raw CounterPoint category code, so it is the label itself.
 */
export type CategoryRow = {
  category: string;
  ty: number;
  ly: number;
  decline: number;
  comp: number | null;
  pct: number | null;
};

export type Categories = {
  months: number;
  window: { ty: { start: string; end: string }; ly: { start: string; end: string } } | null;
  total: { ty: number; ly: number };
  rows: CategoryRow[];
  biggest_slip: { category: string; decline: number; comp: number | null } | null;
};

export type FleetRank = { metric: 'mtd_net_sales'; rank: number; of: number };

export type PerformanceData = {
  store_no: string;
  as_of: string | null;
  fiscal_month_start: string | null;
  fiscal_year_start: string | null;
  computed_at: string | null;
  sections: Sections;
  daily: TrendPoint[];
  categories: Categories;
  fleet_rank: FleetRank | null;
};

export function usePerformance(storeNo?: number): UseQueryResult<PerformanceData, Error> {
  const qs = storeNo !== undefined ? `?store_no=${storeNo}` : '';
  return useQuery<PerformanceData, Error>({
    queryKey: ['stores', 'omni', storeNo ?? 'own'],
    queryFn: () => apiGet<PerformanceData>(`${STORES_API_BASE}/omni${qs}`),
    staleTime: 30_000,
    retry: false,
  });
}
